import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Resource } from '../../../pages/resources';
import { PageContent } from '../../Interfaces/IPageContent';
import DefaultWidthContainer from '../../Layout/Containers/DefaultWidthContainer';
import ImageAndTextContainer from '../../Layout/Containers/ImageAndTextContainer/ImageAndTextContainer';
import LargeWidthContainer from '../../Layout/Containers/LargeWidthContainer';
import { StrapiImageComponent } from '../MediaComponent/MediaComponent';
import styles from './renderBlogPosts.module.scss';
import { StrapiImage } from '../../Models/MediaProps';

export enum ComponentType {
    PIC_LEFT_TEXT_RIGHT = 'paragraphs.picture-left-text-right',
    TEXT_LEFT_PIC_RIGHT = 'paragraphs.text-left-picture-right',
    PARAGRAPH = 'paragraphs.paragraph',
    PARAGRAPH_CENTER = 'paragraphs.center-paragraph',
    DOUBLE_PARAGRAPH = 'paragraphs.double-paragraph',
    PICTURE = 'pictures.picture',
    PICTURE_COVER = 'pictures.cover-picture',
    PICTURE_DOUBLE = 'pictures.double-pictures',
    PICTURE_FULL_WIDTH = 'pictures.full-width-picture',
    YOUTUBE_EMBED = 'video.you-tube-embed',
    PDF = 'files.pdf',
    SEO = 'seo.meta-information',
}

export interface BlogComponent {
    __component: ComponentType;
    id: number;
    heading?: string;
    h1_heading: boolean;
    heading_size: 'small' | 'big';
    paragraph?: string;
    paragraph_size: 'small' | 'big';
    centerParagraph?: string;
    leftParagraph?: string;
    rightParagraph?: string;
    picture?: StrapiImage;
    pictureLeft?: StrapiImage;
    pictureRight?: StrapiImage;
    fullWidthPicture?: StrapiImage;
    youtubeSrc?: string;
    pdf?: PDF;
}

export interface PDF {
    url: string;
}

export interface BlogPostProps {
    blog: Work | Resource | PageContent;
    pictureChildren?: any;
}

const BlogPost = ({ blog, pictureChildren }: BlogPostProps) => {
    if (!blog.content?.length) {
        return null;
    } else {
        return (
            <div>
                {blog.content.map((entry: BlogComponent) => {
                    switch (entry.__component) {
                        case ComponentType.PIC_LEFT_TEXT_RIGHT:
                            return entry.picture && entry.paragraph ? (
                                <div
                                    className={styles.imageAndTextContainer}
                                    key={`pic-left-text-right-${entry.id}`}
                                >
                                    <ImageAndTextContainer
                                        strapiImage={entry.picture}
                                        ingress={entry.heading}
                                        h1={entry.h1_heading}
                                        ingressSize={entry.heading_size}
                                        paragraphSize={entry.paragraph_size}
                                        markdown={entry.paragraph}
                                        pictureFirstOrLast="first"
                                        largeContainer={true}
                                    />
                                </div>
                            ) : null;
                        case ComponentType.TEXT_LEFT_PIC_RIGHT:
                            return entry.picture && entry.paragraph ? (
                                <div
                                    className={styles.imageAndTextContainer}
                                    key={`text-left-picture-right-${entry.id}`}
                                >
                                    <ImageAndTextContainer
                                        key={`text-left-pic-right-${entry.id}`}
                                        strapiImage={entry.picture}
                                        ingress={entry.heading}
                                        h1={entry.h1_heading}
                                        ingressSize={entry.heading_size}
                                        paragraphSize={entry.paragraph_size}
                                        markdown={entry.paragraph}
                                        pictureFirstOrLast="last"
                                        largeContainer={true}
                                    />
                                </div>
                            ) : null;
                        case ComponentType.PARAGRAPH:
                            return entry.paragraph ? (
                                <DefaultWidthContainer key={`paragraph-${entry.id}`}>
                                    <div className={styles.textContainer}>
                                        {entry.heading && (
                                            <h2 className={styles.h2}>{entry.heading}</h2>
                                        )}
                                        <ReactMarkdown>{entry.paragraph}</ReactMarkdown>
                                    </div>
                                </DefaultWidthContainer>
                            ) : null;
                        case ComponentType.PARAGRAPH_CENTER:
                            return entry.centerParagraph ? (
                                <DefaultWidthContainer key={`paragraph-center-${entry.id}`}>
                                    <div className={styles.textContainerCenter}>
                                        <div>
                                            {entry.heading && (
                                                <h2 className={styles.h2}>{entry.heading}</h2>
                                            )}
                                            <ReactMarkdown>{entry.centerParagraph}</ReactMarkdown>
                                        </div>
                                    </div>
                                </DefaultWidthContainer>
                            ) : null;
                        case ComponentType.DOUBLE_PARAGRAPH:
                            return entry.leftParagraph && entry.rightParagraph ? (
                                <DefaultWidthContainer key={`double-paragraph-${entry.id}`}>
                                    <div className={styles.doubleParagraphContainer}>
                                        <div>
                                            <ReactMarkdown>{entry.leftParagraph}</ReactMarkdown>
                                        </div>
                                        <div className={styles.rightParagraph}>
                                            <ReactMarkdown>{entry.rightParagraph}</ReactMarkdown>
                                        </div>
                                    </div>
                                </DefaultWidthContainer>
                            ) : null;
                        case ComponentType.PICTURE:
                            return entry.picture ? (
                                <LargeWidthContainer key={`picture-${entry.id}`}>
                                    <div className={styles.flexFullCenter}>
                                        <ReactMarkdown className={styles.videoHeading}>
                                            {entry.heading}
                                        </ReactMarkdown>
                                        <StrapiImageComponent
                                            maxWidth={entry.picture.width}
                                            maxHeight={entry.picture.height}
                                            strapiImage={entry.picture}
                                        />
                                        {pictureChildren ? pictureChildren : null}
                                    </div>
                                </LargeWidthContainer>
                            ) : null;
                        case ComponentType.PICTURE_DOUBLE:
                            return entry.pictureLeft && entry.pictureRight ? (
                                <LargeWidthContainer
                                    key={`double-pictures-${entry.id}`}
                                    extraClassNames={styles.doublePicturesContainer}
                                >
                                    <div>
                                        <StrapiImageComponent
                                            maxWidth={
                                                entry.pictureLeft.width > 798
                                                    ? 798
                                                    : entry.pictureLeft.width
                                            }
                                            maxHeight={
                                                entry.pictureLeft.height > 450
                                                    ? 450
                                                    : entry.pictureLeft.height
                                            }
                                            strapiImage={entry.pictureLeft}
                                        />
                                    </div>
                                    <div>
                                        <StrapiImageComponent
                                            maxWidth={
                                                entry.pictureRight.width > 798
                                                    ? 798
                                                    : entry.pictureRight.width
                                            }
                                            maxHeight={
                                                entry.pictureRight.height > 450
                                                    ? 450
                                                    : entry.pictureRight.height
                                            }
                                            strapiImage={entry.pictureRight}
                                        />
                                    </div>
                                </LargeWidthContainer>
                            ) : null;
                        case ComponentType.PICTURE_FULL_WIDTH:
                            return entry.fullWidthPicture ? (
                                <div
                                    key={`full-width-picture-${entry.id}`}
                                    className={styles.flexFullCenter}
                                >
                                    <StrapiImageComponent
                                        maxWidth={entry.fullWidthPicture.width}
                                        maxHeight={entry.fullWidthPicture.height}
                                        strapiImage={entry.fullWidthPicture}
                                    />
                                </div>
                            ) : null;
                        case ComponentType.YOUTUBE_EMBED:
                            return entry.youtubeSrc ? (
                                <LargeWidthContainer
                                    key={`youtube-embed-${entry.id}`}
                                    extraClassNames={styles.videoContainer}
                                >
                                    <ReactMarkdown className={styles.videoHeading}>
                                        {entry.heading}
                                    </ReactMarkdown>
                                    <iframe
                                        src={entry.youtubeSrc}
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        className={styles.videoEmbed}
                                    />
                                </LargeWidthContainer>
                            ) : null;
                        case ComponentType.PDF:
                            return entry.pdf && entry.pdf.url ? (
                                <DefaultWidthContainer key={`pdf-link-${entry.id}`}>
                                    <div className={styles.downloadButton}>
                                        <a
                                            href={process.env.NEXT_PUBLIC_IMAGE_URL + entry.pdf.url}
                                            className={styles.downloadButton}
                                        >
                                            <img src="/images/download.svg" />
                                        </a>
                                    </div>
                                </DefaultWidthContainer>
                            ) : null;
                        default:
                            return null;
                    }
                })}
            </div>
        );
    }
};

export default BlogPost;
